import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../store/store';
import { HEADER_MESSAGE_TYPES } from '../utils/constants/header';
import {
  requestEnrichmentExport,
  appendEnrichmentParams,
  EXPORT_MODE
} from '../components/profiler/CompanyDetails/utils/enrichmentContactsDownload';
import useEnrichmentJobPolling from './useEnrichmentJobPolling';
import useEnrichmentAccess from './useEnrichmentAccess';

const TOAST = {
  INSUFFICIENT: 'Insufficient enrichment credits for this download.',
  GENERIC: 'Could not download contacts. Please try again.'
};

/**
 * Orchestrates the v3 async XLSX export (TD-2101 AC-8).
 *
 * State machine:
 *   startDownload(fetch_missing)
 *     → 'downloaded'   : 200 binary, done
 *     → 'pending'      : 202 jobId → useEnrichmentJobPolling
 *         → job 'completed' → re-call export(cached_only) → 'downloaded'
 *         → job 'failed'    → toast + 'error'
 *     → 'insufficient' : 402 → toast + 'error'
 *     → 'error'        : transport/unknown → toast + 'error'
 *
 * Scope-agnostic: the caller supplies `resolveBaseUrl()` returning the export
 * URL already carrying companyId/projectId (company → /export-profiler-company-
 * contacts, project → /export-contacts-list).
 *
 * @param {object} cfg
 * @param {() => Promise<string>} cfg.getToken
 * @param {() => string} cfg.resolveBaseUrl
 * @param {string} cfg.filename
 * @param {string} cfg.contentType
 * @param {string} [cfg.linkId]
 * @param {() => void} [cfg.onComplete]  fired once a binary lands (caller closes dialog)
 * @param {(result: object) => void} [cfg.onError]
 */
const useEnrichmentContactsDownload = ({
  getToken,
  resolveBaseUrl,
  filename,
  contentType,
  linkId,
  onComplete,
  onError,
  requestExport = requestEnrichmentExport
}) => {
  const store = useStore();
  // Every export outcome that may have moved a balance reconciles through this
  // refetch (see reconcileBalance). TD-2100 AC-4 started it for the 402, whose
  // badge stays visible while the dialog is in its error phase.
  //
  // No dialog-open gate: the shared subscription snapshot makes a background
  // load a no-op once any page consumer has loaded it, and every page that
  // mounts these dialogs already has an unconditional status reader.
  const { refetch: refetchAccess } = useEnrichmentAccess();
  const [phase, setPhase] = useState('idle');
  const [jobId, setJobId] = useState(null);

  // Selected fields/skip for the in-flight job — reused verbatim for the
  // cached_only retrieval call after the Surfe job completes.
  const requestRef = useRef(null);
  // Blocks the terminal handler from double-firing the cached_only re-call
  // (Strict Mode double-invoke, or a stray re-render landing on 'completed'
  // before jobId clears).
  const terminalHandledRef = useRef(false);
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const { status: jobStatus, job } = useEnrichmentJobPolling(jobId);

  const toast = useCallback(
    (message) => store.utilsStore?.setHeaderMessage?.(message, HEADER_MESSAGE_TYPES.ERROR),
    [store]
  );

  const doExport = useCallback(
    async (mode) => {
      // Wrap the whole thing: getToken can reject, and appendEnrichmentParams'
      // `new URL(...)` throws synchronously on a malformed base URL. Without this
      // a throw escapes as an unhandled rejection, bypassing finishWith.
      try {
        const token = await getToken();
        const url = appendEnrichmentParams(resolveBaseUrl(), { ...requestRef.current, mode });
        return await requestExport({
          url,
          token,
          defaultFilename: filename,
          defaultContentType: contentType,
          linkId
        });
      } catch {
        return { status: 'error' };
      }
    },
    [getToken, resolveBaseUrl, filename, contentType, linkId, requestExport]
  );

  // Reconcile the shared subscription snapshot whenever the BE may have
  // moved a balance, so the Contact credits and Downloads counters do not wait
  // for a reload. That is a delivered file (credits for fetched fields, plus the
  // project's downloads), a 402 (TD-2100: the badge stays visible during the
  // error phase), and any outcome once a Surfe job has run, because the job
  // deducts credits even when the job or the cached_only retrieval then fails.
  // A transport error on the first submit implies no change. Called before
  // the unmount guards: the snapshot outlives this dialog.
  const reconcileBalance = useCallback(
    (result, { afterJob = false } = {}) => {
      if (afterJob || result.status === 'downloaded' || result.status === 'insufficient') {
        refetchAccess?.();
      }
    },
    [refetchAccess]
  );

  const finishWith = useCallback(
    (result) => {
      if (result.status === 'downloaded') {
        setPhase('downloaded');
        onComplete?.();
        return;
      }
      toast(result.status === 'insufficient' ? TOAST.INSUFFICIENT : TOAST.GENERIC);
      setPhase('error');
      onError?.(result);
    },
    [toast, onComplete, onError]
  );

  const startDownload = useCallback(
    async ({ enrichmentFields, skipPreviouslyEnriched, roleBuckets }) => {
      requestRef.current = { enrichmentFields, skipPreviouslyEnriched, roleBuckets };
      terminalHandledRef.current = false;
      setPhase('submitting');
      const result = await doExport(EXPORT_MODE.FETCH_MISSING);
      if (result.status !== 'pending') reconcileBalance(result);
      if (!isMounted.current) return;
      if (result.status === 'pending') {
        setJobId(result.jobId);
        setPhase('pending');
        return;
      }
      finishWith(result);
    },
    [doExport, finishWith, reconcileBalance]
  );

  // Terminal-state handler kept in a ref so the effect can depend only on
  // jobStatus (fires exactly once per transition, immune to callback identity
  // churn from inline parent props).
  const handleTerminalRef = useRef(() => {});
  handleTerminalRef.current = async (status) => {
    if (terminalHandledRef.current) return;
    terminalHandledRef.current = true;
    setJobId(null);
    if (status === 'failed') {
      reconcileBalance({ status: 'error' }, { afterJob: true });
      toast(TOAST.GENERIC);
      setPhase('error');
      onError?.({ status: 'error' });
      return;
    }
    // completed → retrieve the now-cached binary
    const result = await doExport(EXPORT_MODE.CACHED_ONLY);
    reconcileBalance(result, { afterJob: true });
    if (!isMounted.current) return;
    finishWith(result);
  };

  useEffect(() => {
    if (jobStatus === 'completed' || jobStatus === 'failed') {
      handleTerminalRef.current(jobStatus);
    }
  }, [jobStatus]);

  const reset = useCallback(() => {
    setPhase('idle');
    setJobId(null);
    requestRef.current = null;
    terminalHandledRef.current = false;
  }, []);

  return {
    phase,
    startDownload,
    reset,
    isDownloading: phase === 'submitting' || phase === 'pending',
    // Live enrichment progress from the status poll (TD-2307). Surfaced so the
    // dialog can render "Enriching… X / Y" during the BE Surfe job; undefined
    // before the first poll lands (dialog falls back to plain "Enriching…").
    enrichedCount: job?.enrichedCount,
    totalContacts: job?.totalContacts
  };
};

export default useEnrichmentContactsDownload;
